import type { Role, RoleId } from '@ipfabric/utils';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { Button } from 'components/Button/Button';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';

export const rolesApiEndpoint = '/tables/roles';

const isBuiltinRole = (role: Role) => !role.isAdmin && role.isSystem;
const isCustomRole = (role: Role) => !role.isSystem;

interface Props {
  onEditRole: (roleId: RoleId) => void;
  onCopyRole: (roleId: RoleId) => void;
  onRemoveRole: (roleId: RoleId) => void;
}

export const RolesTable: FC<Props> = (props) => {
  const { administrationRolesMessages: rolesMsg } = useLocale();

  const endpoint = useTableEndpoint({
    apiEndpoint: rolesApiEndpoint,
    alternative: (origEndpoint) => ({
      id: origEndpoint.id,
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: rolesMsg.actionColumnTitle,
          webDependencies: ['id', 'isAdmin', 'isSystem'],
          render: (rowData: Role) => (
            <div>
              {isBuiltinRole(rowData) && (
                <Button
                  variant='tertiary'
                  accent='positive'
                  icon='fa-eye'
                  size='xxs'
                  onClick={() => {
                    props.onEditRole(rowData.id);
                  }}
                  type='button'
                />
              )}
              {isCustomRole(rowData) && (
                <Button
                  variant='tertiary'
                  accent='positive'
                  icon='fa-pen'
                  size='xxs'
                  onClick={() => {
                    props.onEditRole(rowData.id);
                  }}
                  type='button'
                />
              )}
              {isCustomRole(rowData) && (
                <Button
                  variant='tertiary'
                  accent='positive'
                  icon='fa-copy'
                  size='xxs'
                  onClick={() => {
                    props.onCopyRole(rowData.id);
                  }}
                  type='button'
                />
              )}
              {isCustomRole(rowData) && (
                <Button
                  variant='tertiary'
                  icon='fa-trash-can'
                  size='xxs'
                  onClick={() => {
                    props.onRemoveRole(rowData.id);
                  }}
                  type='button'
                  dataTestId='delete-button'
                />
              )}
            </div>
          ),
        }),
      ],
    }),
  });

  const rolesApiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      initialSort: { column: 'name', order: 'asc' },
    },
  });

  return <ApiTablePageView model={rolesApiTablePage} />;
};
